import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../constants/appConstants';

type Theme = 'light' | 'dark';

interface BetaThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const BetaThemeContext = createContext<BetaThemeContextType | undefined>(undefined);

export const BetaThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.BETA_THEME);
    return (stored === 'dark' || stored === 'light') ? stored : 'light';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.BETA_THEME, theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  return (
    <BetaThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </BetaThemeContext.Provider>
  );
};

export const useBetaTheme = (): BetaThemeContextType => {
  const context = useContext(BetaThemeContext);
  if (!context) {
    throw new Error('useBetaTheme must be used within a BetaThemeProvider');
  }
  return context;
};
